import { AutonomousRootSet, LayoutGraph } from './types';

/**
 * Builds a transitive backward adjacency map where each node maps to ALL its ancestors,
 * not just direct predecessors. Uses BFS from each node to find all reachable ancestors.
 *
 * @param backwardMap Direct backward adjacency (node -> immediate predecessors)
 * @param nodeIds All nodes to process
 * @returns Map where each node maps to a Set of all ancestor nodes (including itself)
 */
function buildTransitiveBackwardMap(backwardMap: Map<number, number[]>, nodeIds: number[]): Map<number, Set<number>> {
    const transitiveMap = new Map<number, Set<number>>();

    // For each node, do a backward BFS to find all ancestors
    for (const nodeId of nodeIds) {
        const ancestors = new Set<number>();
        const queue = [nodeId];
        const visited = new Set<number>([nodeId]);

        while (queue.length > 0) {
            const current = queue.shift()!;
            ancestors.add(current);

            const predecessors = backwardMap.get(current) || [];
            for (const pred of predecessors) {
                if (!visited.has(pred)) {
                    visited.add(pred);
                    queue.push(pred);
                }
            }
        }

        transitiveMap.set(nodeId, ancestors);
    }

    return transitiveMap;
}

function setsOverlap<T>(set1: Set<T>, set2: Set<T>): boolean {
    for (const item of set1) {
        if (set2.has(item)) {
            return true;
        }
    }
    return false;
}

/**
 * Finds autonomous root sets - groups of root nodes whose ancestor trees don't overlap.
 *
 * Root nodes are nodes with no outgoing connections (terminal/leaf nodes on the right).
 * Autonomous root sets are groups of roots where their ancestors don't overlap with
 * ancestors of roots in other groups.
 *
 * Disconnected nodes (nodes with no connections) are treated as individual autonomous roots.
 */
export function findAutonomousRoots(graph: LayoutGraph, nodeIdsToProcess: number[]): AutonomousRootSet[] {
    const nodeIdSet = new Set(nodeIdsToProcess);

    // Initialize adjacency maps for all nodes in nodeIdsToProcess
    const backwardMap = new Map<number, number[]>();
    const forwardMap = new Map<number, number[]>();

    for (const nodeId of nodeIdsToProcess) {
        backwardMap.set(nodeId, []);
        forwardMap.set(nodeId, []);
    }

    // Build adjacency maps in a single pass (only for nodes in nodeIdsToProcess)
    for (const conn of graph.connections) {
        if (nodeIdSet.has(conn.sendingNodeId) && nodeIdSet.has(conn.receivingNodeId)) {
            backwardMap.get(conn.receivingNodeId)!.push(conn.sendingNodeId);
            forwardMap.get(conn.sendingNodeId)!.push(conn.receivingNodeId);
        }
    }

    // Root nodes are those with no outgoing connections (empty forwardMap)
    const rootNodes = nodeIdsToProcess.filter(id => forwardMap.get(id)!.length === 0);

    // Build transitive backward map once (pre-compute all ancestors for all nodes)
    const backwardTransitive = buildTransitiveBackwardMap(backwardMap, nodeIdsToProcess);

    // Get ancestors for each root (simple lookup - no BFS needed!)
    const rootAncestors = new Map<number, Set<number>>();
    for (const rootId of rootNodes) {
        rootAncestors.set(rootId, backwardTransitive.get(rootId)!);
    }

    // Group roots whose ancestor sets overlap using union-find approach
    const rootToGroup = new Map<number, number>();
    let nextGroupId = 0;

    for (const rootId of rootNodes) {
        const ancestors = rootAncestors.get(rootId)!;

        // Find if this root's ancestors overlap with any existing group
        let foundGroup: number | null = null;

        for (const otherRootId of rootNodes) {
            if (otherRootId === rootId) continue;
            if (!rootToGroup.has(otherRootId)) continue;

            const otherAncestors = rootAncestors.get(otherRootId)!;

            // Check for overlap using helper function
            if (setsOverlap(ancestors, otherAncestors)) {
                foundGroup = rootToGroup.get(otherRootId)!;
                break;
            }
        }

        if (foundGroup !== null) {
            rootToGroup.set(rootId, foundGroup);
        } else {
            rootToGroup.set(rootId, nextGroupId++);
        }
    }

    // Merge groups if they should be together (transitive closure)
    // If root A and B share ancestors, and B and C share ancestors, then A, B, C should be together
    let changed = true;
    while (changed) {
        changed = false;
        for (const rootId1 of rootNodes) {
            for (const rootId2 of rootNodes) {
                if (rootId1 === rootId2) continue;

                const ancestors1 = rootAncestors.get(rootId1)!;
                const ancestors2 = rootAncestors.get(rootId2)!;

                // Check for overlap using helper function
                if (setsOverlap(ancestors1, ancestors2)) {
                    const group1 = rootToGroup.get(rootId1)!;
                    const group2 = rootToGroup.get(rootId2)!;

                    if (group1 !== group2) {
                        // Merge groups - assign all nodes in group2 to group1
                        const minGroup = Math.min(group1, group2);
                        const maxGroup = Math.max(group1, group2);

                        for (const [rootId, groupId] of rootToGroup.entries()) {
                            if (groupId === maxGroup) {
                                rootToGroup.set(rootId, minGroup);
                                changed = true;
                            }
                        }
                    }
                }
            }
        }
    }

    // Build autonomous root sets
    const groupMap = new Map<number, AutonomousRootSet>();

    for (const [rootId, groupId] of rootToGroup.entries()) {
        if (!groupMap.has(groupId)) {
            groupMap.set(groupId, {
                rootIds: [],
                nodeIds: new Set(),
            });
        }

        const group = groupMap.get(groupId)!;
        group.rootIds.push(rootId);

        // Add all ancestors
        const ancestors = rootAncestors.get(rootId)!;
        for (const nodeId of ancestors) {
            group.nodeIds.add(nodeId);
        }
    }

    return Array.from(groupMap.values());
}
